import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table, Statistic, Button, Spin, Modal, Alert } from 'antd';
import { Link, useParams } from 'react-router-dom';
import { UnitCategoriesApi } from 'api/api';
import {
  UnitCategoryBasicInfoDto,
  CategoryStatisticsDto,
  UnitCategoryFloorDto,
  UnitDto,
  UnitAttachmentDto,
  UnitBriefDto,
} from 'api/models';
import { useTranslation } from 'react-i18next';
import FeatherIcon from 'feather-icons-react';
import { PageHeader } from 'components/page-headers/page-headers';
import { CardToolbox, Main } from 'container/styled';
import { hasPermission } from 'utility/accessibility/hasPermission';
import { UnitCategoryForm } from './UnitCategoryForm';
import { BuildingVisualization } from './components/BuildingVisualization';
import { FloorUnitsModal } from './components/FloorUnitsModal';
import openNotificationWithIcon from 'utility/notification';
import styled from 'styled-components';
import { PlusOutlined } from '@ant-design/icons';
import { UnitCreateModal } from './components/UnitCreateModal';
import { formatSquareMeters } from 'utility/utility';
import { ImageGalleryModal } from 'components/ImageGalleryModal';

const categoriesApi = new UnitCategoriesApi();

const SplitViewContainer = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 24px;
`;

const TableSection = styled.div`
  flex: 2;
  min-width: 0;
`;

const VisualizationSection = styled.div`
  flex: 1;
  min-width: 400px;
  max-width: 600px;
  background: #f0f2f5;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

export const UnitCategoryDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();

  // Split state for different data sections
  const [category, setCategory] = useState<UnitCategoryBasicInfoDto>();
  const [statistics, setStatistics] = useState<CategoryStatisticsDto>();
  const [floors, setFloors] = useState<UnitCategoryFloorDto[]>([]);
  const [units, setUnits] = useState<UnitBriefDto[]>([]);

  // Loading states for different sections
  const [basicLoading, setBasicLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [floorsLoading, setFloorsLoading] = useState(false);
  const [unitsLoading, setUnitsLoading] = useState(false);

  const [editModalVisible, setEditModalVisible] = useState(false);
  const [unitModalVisible, setUnitModalVisible] = useState(false);
  const [selectedFloor, setSelectedFloor] = useState<UnitCategoryFloorDto | null>(null);
  const [galleryUnit, setGalleryUnit] = useState<{ unitId: number; images: UnitAttachmentDto[] } | null>(null);

  // Fetch basic category info first (lightweight)
  const fetchBasicCategoryInfo = async () => {
    if (!id) return;
    try {
      setBasicLoading(true);
      const response = await categoriesApi.apiUnitCategoriesIdGet({ id: parseInt(id) });
      setCategory(response.data);
    } catch (error) {
      openNotificationWithIcon('error', t('categories:fetch-error', 'Failed to fetch category details'));
    } finally {
      setBasicLoading(false);
    }
  };

  // Fetch statistics
  const fetchStatistics = async () => {
    if (!id) return;
    try {
      setStatsLoading(true);
      const response = await categoriesApi.apiUnitCategoriesIdStatisticsGet({ id: parseInt(id) });
      setStatistics(response.data);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  // Fetch floors data
  const fetchFloors = async () => {
    if (!id) return;
    try {
      setFloorsLoading(true);
      const response = await categoriesApi.apiUnitCategoriesIdFloorsGet({ id: parseInt(id) });
      setFloors(response.data);
    } catch (error) {
      console.error('Failed to fetch floors:', error);
    } finally {
      setFloorsLoading(false);
    }
  };

  // Fetch units data
  const fetchUnits = async () => {
    if (!id) return;
    try {
      setUnitsLoading(true);
      const response = await categoriesApi.apiUnitCategoriesIdUnitsGet({ id: parseInt(id) });
      setUnits(response.data);
    } catch (error) {
      console.error('Failed to fetch units:', error);
    } finally {
      setUnitsLoading(false);
    }
  };

  // Load all data
  const fetchAllData = async () => {
    if (!id) return;

    // Load basic info first
    await fetchBasicCategoryInfo();

    // Then load other data in parallel
    await Promise.all([fetchStatistics(), fetchFloors(), fetchUnits()]);
  };

  useEffect(() => {
    fetchAllData();
  }, [id]);

  const handleUpdate = async () => {
    try {
      await fetchAllData();
      setEditModalVisible(false);
    } catch (error) {
      openNotificationWithIcon('error', t('categories:update-error', 'Error updating category'));
    }
  };

  // Statistics cards data
  const statisticsCards = [
    {
      title: t('categories:total-units', 'Total Units'),
      value: statistics?.totalUnits || 0,
      icon: 'box',
      color: '#1890ff',
    },
    {
      title: t('categories:available-units', 'Available Units'),
      value: statistics?.availableUnits || 0,
      icon: 'check-square',
      color: '#52c41a',
    },
    {
      title: t('categories:total-area', 'Total Area'),
      value: `${statistics?.totalSquareMeters?.toFixed(2) || '0.00'} m²`,
      icon: 'square',
      color: '#faad14',
    },
    {
      title: t('categories:deals', 'Total Deals'),
      value: statistics?.dealsCount || 0,
      icon: 'shopping-bag',
      color: '#f5222d',
    },
  ];

  const floorColumns = [
    {
      title: t('categories:floor-number', 'Floor Number'),
      dataIndex: 'floorNumber',
      key: 'floorNumber',
      sorter: (a: UnitCategoryFloorDto, b: UnitCategoryFloorDto) => (a.floorNumber || 0) - (b.floorNumber || 0),
    },
    {
      title: t('categories:description', 'Description'),
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: t('categories:units-count', 'Units'),
      dataIndex: 'unitsCount',
      key: 'unitsCount',
    },
    {
      title: t('categories:available', 'Available'),
      dataIndex: 'availableUnitsCount',
      key: 'availableUnitsCount',
    },
    {
      title: t('categories:sold', 'Sold'),
      dataIndex: 'soldUnitsCount',
      key: 'soldUnitsCount',
    },
    {
      title: t('categories:reserved', 'Reserved'),
      dataIndex: 'reservedUnitsCount',
      key: 'reservedUnitsCount',
    },
  ];

  const unitColumns = [
    {
      title: t('units:name', 'Name'),
      dataIndex: 'name',
      key: 'name',
      width: 150,
      fixed: 'left' as const,
      render: (value: string, record: UnitBriefDto) =>
        hasPermission('units:view') ? <Link to={`/units/${record.id}`}>{value}</Link> : value,
    },
    {
      title: t('units:square-meters', 'Square Meters'),
      dataIndex: 'squareMeters',
      key: 'squareMeters',
      width: 120,
      render: (value: number) => formatSquareMeters(value),
    },
    ...(category?.hasFloors
      ? [
          {
            title: t('units:available-floors', 'Available Floors'),
            key: 'floors',
            width: 150,
            render: (_: any, record: UnitBriefDto) => {
              if (!record.floors?.length) {
                return '-';
              }

              const availableFloors = record.floors.filter(floor => floor.stock && floor.stock > 0);
              return availableFloors.map(floor => floor.floorNumber).join(', ');
            },
          },
        ]
      : []),
    {
      title: t('units:stock', 'Stock'),
      dataIndex: 'stock',
      key: 'stock',
      width: 80,
    },
    {
      title: t('units:available', 'Available'),
      dataIndex: 'available',
      key: 'available',
      width: 90,
    },
    {
      title: t('units:sold', 'Sold'),
      dataIndex: 'sold',
      key: 'sold',
      width: 80,
    },
    {
      title: t('units:reserved', 'Reserved'),
      dataIndex: 'reserved',
      key: 'reserved',
      width: 90,
    },
    {
      title: t('global:actions', 'Actions'),
      key: 'action',
      width: 150,
      render: (_: any, record: UnitBriefDto) => (
        <div className="table-actions">
          {record.images && record.images.length > 0 && (
            <Button
              type="link"
              icon={<FeatherIcon icon="image" size={16} style={{ marginRight: '4px' }} />}
              onClick={() =>
                setGalleryUnit({
                  unitId: record.id!,
                  images: record.images || [],
                })
              }
            >
              {t('units:gallery', 'Gallery')}
            </Button>
          )}
        </div>
      ),
    },
  ];

  const handleFloorClick = (floor: UnitCategoryFloorDto) => {
    setSelectedFloor(floor);
  };

  // Statistics Cards Component
  const StatisticsCards: React.FC = () => (
    <Row gutter={[16, 16]} justify="center">
      {statisticsCards.map((stat, index) => (
        <Col xs={24} sm={12} md={6} key={index}>
          <Card bordered={false} style={{ textAlign: 'center' }} loading={statsLoading}>
            <FeatherIcon icon={stat.icon} size={24} style={{ marginBottom: 8, color: stat.color }} />
            <Statistic title={stat.title} value={stat.value} />
          </Card>
        </Col>
      ))}
    </Row>
  );

  // Floors Table Component
  const FloorsTable: React.FC = () => (
    <Card title={t('categories:floors', 'Floors')} loading={floorsLoading}>
      <Table
        loading={floorsLoading}
        dataSource={floors}
        columns={floorColumns}
        pagination={false}
        scroll={{ x: 800 }}
      />
    </Card>
  );

  // Units Table Component
  const UnitsTable: React.FC = () => (
    <Card
      title={t('categories:units', 'Units')}
      loading={unitsLoading}
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setUnitModalVisible(true)}>
          {t('units:create-new', 'Create New Unit')}
        </Button>
      }
    >
      <Table
        loading={unitsLoading}
        dataSource={units}
        columns={unitColumns}
        pagination={{
          pageSize: 10,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} ${t('global:of', 'of')} ${total} ${t('units:units', 'units')}`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        scroll={{
          x: 1200, // Horizontal scroll when table is wider than container
          y: 600, // Vertical scroll when table height exceeds 600px
        }}
      />
    </Card>
  );

  // Building Visualization Component
  const BuildingVisualizationCard: React.FC = () => (
    <VisualizationSection>
      {floorsLoading ? (
        <Spin size="large" />
      ) : (
        floors && floors.length > 0 && <BuildingVisualization floors={floors} onFloorClick={handleFloorClick} />
      )}
    </VisualizationSection>
  );

  return (
    <>
      <CardToolbox>
        <PageHeader
          ghost
          title={category?.name}
          subTitle={
            <span>
              <Link to={`/projects/${category?.projectId}`}>{category?.projectName || ''}</Link>
            </span>
          }
          buttons={[
            hasPermission('projects:manage-categories') && (
              <Button
                key="edit"
                type="primary"
                icon={<FeatherIcon icon="edit" size={16} />}
                onClick={() => setEditModalVisible(true)}
              >
                {t('global:edit', 'Edit')}
              </Button>
            ),
          ]}
        />
      </CardToolbox>

      <Main>
        {/* Show basic loading spinner if basic info is still loading */}
        <Spin spinning={basicLoading}>
          {/* Statistics Cards */}
          <StatisticsCards />

          <SplitViewContainer>
            <TableSection>
              {category?.hasFloors ? (
                <FloorsTable />
              ) : (
                !basicLoading && (
                  <Alert
                    message={t('categories:no-floors-message', 'This category does not use floors')}
                    type="info"
                    showIcon
                  />
                )
              )}

              <div style={{ marginTop: '20px' }}>
                <UnitsTable />
              </div>
            </TableSection>

            {category?.hasFloors && <BuildingVisualizationCard />}
          </SplitViewContainer>
        </Spin>

        {/* Edit Modal */}
        {editModalVisible && category && (
          <UnitCategoryForm
            visible={editModalVisible}
            editMode={true}
            category={category as any} // You might need to adapt this based on the form's expected props
            onSuccess={handleUpdate}
            onCancel={() => setEditModalVisible(false)}
          />
        )}

        {/* Floor Units Modal */}
        {selectedFloor && category && (
          <FloorUnitsModal
            visible={true}
            floor={selectedFloor}
            categoryId={category.id!}
            onClose={() => setSelectedFloor(null)}
            onSuccess={fetchAllData}
          />
        )}

        {/* Add Unit Modal */}
        {unitModalVisible && category && (
          <UnitCreateModal
            visible={unitModalVisible}
            categoryId={category.id!}
            categoryHasFloors={category.hasFloors || false}
            floors={floors}
            onCancel={() => setUnitModalVisible(false)}
            onSuccess={fetchAllData}
          />
        )}

        {galleryUnit && (
          <ImageGalleryModal
            visible={!!galleryUnit}
            onClose={() => setGalleryUnit(null)}
            attachments={galleryUnit.images}
          />
        )}
      </Main>
    </>
  );
};
